
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { DataTableHeader } from 'vuetify';
import draggable from 'vuedraggable';
import UsersRepo from '@/services/Users';
import ProfileRepository from '@/services/Users-profile';
import { UserInterface, UserShiftInterface } from '@/services/Users/Interfaces';
import DepartmentRepository from '@/services/Departments';
import { DepartmentInterface } from '@/services/Departments/Interfaces';
import ShiftTeamRepository from '@/services/Calendar/ShiftTeam';
import { ShiftTypeInterface } from '@/services/Calendar/ShiftType/Interfaces';
import ShiftTypeRepository from '@/services/Calendar/ShiftType';
import DeleteShiftTypeDialog from '@/components/dialogs/calendar/shifts/DeleteShiftTypeDialog.vue';
import {
  ShiftCalendarUserInterface, ShiftTeamUserInterface, ShiftTeamUserType,
} from '@/services/Calendar/ShiftTeam/Interfaces';
import { ShiftAutomationInterface } from '@/services/Calendar/ShiftAutomation/Interfaces';
import ShiftAutomationRepository from '@/services/Calendar/ShiftAutomation';
import { BaseProfileDataInterface } from '@/services/Users-profile/interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    draggable,
    DeleteShiftTypeDialog,
    PageHeader,
  },
  inject: [
    'userRepository',
    'profileRepository',
    'departmentRepository',
    'shiftTeamRepository',
    'shiftTypeRepository',
    'shiftAutomationRepository',
  ],
})
export default class Teams extends Vue {
  private readonly userRepository!: UsersRepo;
  private readonly profileRepository!: ProfileRepository;
  private readonly departmentRepository!: DepartmentRepository;
  private readonly shiftTeamRepository!: ShiftTeamRepository;
  private readonly shiftTypeRepository!: ShiftTypeRepository;
  private readonly shiftAutomationRepository!: ShiftAutomationRepository;

  private headers: DataTableHeader[] = [
    { text: 'Name', value: 'name' },
    { text: 'Short Name', value: 'shortName' },
    { text: 'Duration', value: 'duration' },
    { text: 'Start At', value: 'startAt' },
    { text: 'End At', value: 'endAt' },
    { text: 'Text Color', value: 'textColor' },
    { text: 'Background Color', value: 'background' },
    { text: 'Preview', value: 'preview', sortable: false },
    { text: 'Actions', value: 'actions', sortable: false },
  ];
  private shiftAutomationsHeaders: DataTableHeader[] = [
    { text: 'User', value: 'userId' },
    { text: 'Shift Type', value: 'shiftType.name' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];
  private shiftTypes: ShiftTypeInterface[] = [];
  private deleteShiftTypeDialogData: ShiftTypeInterface | false = false;
  private deleteShiftTypeDialog: string = '';
  private shiftAutomations: ShiftAutomationInterface[] = [];
  private deleteShiftAutomationDialogData: ShiftAutomationInterface | false = false;
  private deleteShiftAutomationDialog: string = '';

  private loading: boolean = false;
  private search: string = '';
  private menuDate: boolean = false;
  private btnLoading: boolean = false;
  private date: string = new Date().toISOString().substr(0, 7);
  private minDate: string = this.date;
  private shiftLeaders: ShiftCalendarUserInterface[] = [];
  private unsortedUsers: UserShiftInterface[] = [];
  private selectedDepartment: string = this.$store.getters.departmentId;
  private departments: DepartmentInterface[] = [];
  private usersMap: { [userId: string]: BaseProfileDataInterface } = {};

  public async mounted(): Promise<void> {
    this.loading = true;
    this.shiftTypes = await this.shiftTypeRepository.getAllShiftTypes();
    this.shiftAutomations = await this.shiftAutomationRepository.getAllShiftAutomations();
    const usersIds = this.shiftAutomations.map((value) => value.userId);
    const usersProfiles = await this.profileRepository.getBaseProfileDataByUsersIds(usersIds);
    usersProfiles.forEach((profile) => {
      this.usersMap[profile.userId] = profile;
    });

    await this.initializeDepartments();
    await this.initializeUsersFromDepartment(this.selectedDepartment);

    this.loading = false;
  }

  private async initializeDepartments(): Promise<void> {
    try {
      const departments = await this.departmentRepository.getAllDepartments();

      departments.map((department: DepartmentInterface) => {
        this.departments.push(department);
      });
    } catch (err) {
      this.$errorDialog.show('Could not retrieve departments.');
    }
  }

  private async initializeUsersFromDepartment(departmentId: string): Promise<void> {
    try {
      const users: UserShiftInterface[] = await this.userRepository.getUsersByDepartment(departmentId);
      const teams = await this.shiftTeamRepository.getAllShiftTeams(departmentId, new Date(this.date));
      this.shiftLeaders = [];
      this.unsortedUsers = [];

      const usersIds = users.map((user) => user.id);
      const usersProfiles = await this.profileRepository.getBaseProfileDataByUsersIds(usersIds);
      for (const user of users) {
        const i = users.indexOf(user);
        user.details = usersProfiles.filter((profile) => user.id === profile.userId)[0];
        users[i] = user;
      }

      teams.forEach((team) => {
        team.shiftTeamUsers = team.shiftTeamUsers
          .sort((user) => user.userType === ShiftTeamUserType.SHIFT_LEADER ? 0 : 1)
          .map((teamUser) => {
            const userData = users.find((user) => user.id === teamUser.userId);
            users.splice(users.findIndex((user) => user.id === teamUser.userId), 1);

            return {
              ...teamUser,
              details: userData?.details,
            }
          });

        this.shiftLeaders.push(team);
      });

      this.unsortedUsers = users;
    } catch (err) {
      this.$errorDialog.show('Could not retrieve users.');
      console.error(err);
    }
  }

  private async createNewList(user: UserShiftInterface): Promise<void> {
    this.unsortedUsers = this.unsortedUsers.filter((u) => u.id !== user.id);
    this.shiftLeaders.push({
      id: 0,
      date: new Date(this.date),
      departmentId: this.selectedDepartment,
      shiftTeamUsers: [{
        userId: user.id,
        details: user.details,
        userType: ShiftTeamUserType.SHIFT_LEADER,
      }],
    } as unknown as ShiftCalendarUserInterface);
  }

  private async updateSelectedDepartment(val: string): Promise<void> {
    this.loading = true;
    this.selectedDepartment = val;

    await this.initializeUsersFromDepartment(this.selectedDepartment);

    this.loading = false;
  }

  private async updateSelectedDate(val: string): Promise<void> {
    this.loading = true;
    this.date = val;

    await this.initializeUsersFromDepartment(this.selectedDepartment);

    this.loading = false;
  }

  private async updateTeams(): Promise<void> {
    this.btnLoading = true;

    for (const team of this.shiftLeaders) {
      if (!team.shiftTeamUsers) { return; }

      const shiftTeamUsers: ShiftTeamUserInterface[] = team.shiftTeamUsers.map((user) => {
        if (typeof user.id !== 'number') {
          return {
            id: undefined,
            userId: user.userId ? user.userId : user.id,
            userType: user.userType ? user.userType : ShiftTeamUserType.USER,
            teamId: team.id,
          } as unknown as ShiftTeamUserInterface;
        }

        return {
          id: user.id,
          userId: user.userId,
          userType: user.userType,
          teamId: team.id,
        } as ShiftTeamUserInterface;
      });

      await this.shiftTeamRepository.createShiftTeam({
        id: team.id > 0 ? team.id : undefined,
        departmentId: this.selectedDepartment,
        date: new Date(this.date),
        shiftTeamUsers,
      });

      this.$successDialog.show('The teams have been successfully saved.');

      await this.initializeUsersFromDepartment(this.selectedDepartment);
    }

    this.btnLoading = false;
  }

  private async deleteTeam(team: ShiftCalendarUserInterface, index: number): Promise<void> {
    if (team.id > 0) {
      await this.shiftTeamRepository.deleteShiftTeam(team.id);

      if (team.shiftTeamUsers?.length && team.shiftTeamUsers[0].details) {
        this.$successDialog.show(`The team with shift leader "${team.shiftTeamUsers[0].details.firstName} ${team.shiftTeamUsers[0].details.lastName}" has been removed successfully.`);
      }
    }

    const deletedTeam: ShiftCalendarUserInterface[] = this.shiftLeaders.splice(index, 1);

    if (!deletedTeam[0].shiftTeamUsers) { return; }

    deletedTeam[0].shiftTeamUsers.forEach((user) => {
      this.unsortedUsers.push(user as unknown as UserInterface);
    });
  }

  private createShiftType(): void {
    this.$router.push({ name: 'calendar_crate_shift_type' });
  }

  private editShiftType(shiftType: ShiftTypeInterface): void {
    this.$router.push({ name: 'calendar_edit_shift_type', params: { shiftTypeId: String(shiftType.id) } });
  }

  private async deleteShiftType(shiftType: ShiftTypeInterface): Promise<void> {
    this.deleteShiftTypeDialog = 'delete-shift-type-dialog';
    this.deleteShiftTypeDialogData = shiftType;
  }

  private filterShiftTypes(id: number): void {
    this.shiftTypes = this.shiftTypes.filter((shiftType: ShiftTypeInterface) => {
      return shiftType.id !== id;
    });
  }

  private createShiftAutomation(): void {
    this.$router.push({ name: 'calendar_crate_shift_automation' });
  }

  private editShiftAutomation(shiftAutomation: ShiftAutomationInterface): void {
    this.$router.push({ name: 'calendar_edit_shift_automation', params: { id: String(shiftAutomation.id) } });
  }

  private async deleteShiftAutomation(shiftAutomation: ShiftAutomationInterface): Promise<void> {
    this.deleteShiftAutomationDialog = 'delete-shift-automation-dialog';
    this.deleteShiftAutomationDialogData = shiftAutomation;
  }

  private filterShiftAutomations(id: number): void {
    this.shiftAutomations = this.shiftAutomations.filter((shiftAutomation: ShiftAutomationInterface) => {
      return shiftAutomation.id !== id;
    });
  }
}
